import { apiClient } from '@/core/api/client';
import type {
  ConfigVersion,
  Impact,
  ImpactSample,
  PlanDetail,
  PlanList,
  PlanListFilters,
  RankingConfigParams,
  Sandbox,
} from '../types/navigator-tuning.type';

const CONFIG_VERSIONS = '/admin/navigator-tuning/config-versions';
const PLANS = '/admin/navigator-tuning/plans';

const isStatus = (error: unknown, status: number) =>
  (error as { response?: { status?: number } })?.response?.status === status;

/** True for the 409 the backend returns when the draft revision is stale. */
export const isStaleDraftError = (error: unknown) => isStatus(error, 409);

export async function listConfigVersions(): Promise<ConfigVersion[]> {
  const response = await apiClient.get<{ versions: ConfigVersion[] }>(CONFIG_VERSIONS);
  return response.data.versions;
}

export async function getLiveConfigVersion(): Promise<ConfigVersion> {
  const response = await apiClient.get<ConfigVersion>(`${CONFIG_VERSIONS}/live`);
  return response.data;
}

/** null when there is no draft (backend 404). */
/** null when there is no draft (200 with an empty body). */
export async function getDraftConfigVersion(): Promise<ConfigVersion | null> {
  const response = await apiClient.get<ConfigVersion | ''>(`${CONFIG_VERSIONS}/draft`);
  return response.data || null;
}

/** 409 if a draft already exists. */
export async function createDraft(params: RankingConfigParams): Promise<ConfigVersion> {
  const response = await apiClient.post<ConfigVersion>(`${CONFIG_VERSIONS}/draft`, { params });
  return response.data;
}

/** 409 if `revision` is stale (another admin saved first) or the draft is gone. */
export async function saveDraft(id: string, params: RankingConfigParams, revision: number): Promise<ConfigVersion> {
  const response = await apiClient.put<ConfigVersion>(`${CONFIG_VERSIONS}/draft/${id}`, { params, revision });
  return response.data;
}

export async function discardDraft(id: string): Promise<void> {
  await apiClient.delete(`${CONFIG_VERSIONS}/draft/${id}`);
}

/** Requires admin:tune-navigator. 409 on a stale revision. */
export async function publishDraft(id: string, revision: number, justification: string): Promise<ConfigVersion> {
  const response = await apiClient.post<ConfigVersion>(`${CONFIG_VERSIONS}/draft/${id}/publish`, {
    revision,
    justification,
  });
  return response.data;
}

/** Requires admin:tune-navigator. Publishes a new version with archived version `id`'s params. */
export async function rollbackToVersion(id: string, justification: string): Promise<ConfigVersion> {
  const response = await apiClient.post<ConfigVersion>(`${CONFIG_VERSIONS}/${id}/rollback`, { justification });
  return response.data;
}

export async function listPlans(filters: PlanListFilters): Promise<PlanList> {
  const response = await apiClient.get<PlanList>(PLANS, { params: filters });
  return response.data;
}

/** Opening a plan is audited by the backend. */
export async function getPlan(sessionId: string): Promise<PlanDetail> {
  const response = await apiClient.get<PlanDetail>(`${PLANS}/${sessionId}`);
  return response.data;
}

/** Replays a complete plan with its config (Baseline) and `params` (Tuned). 409 = pre-tuning. */
export async function runSandbox(sessionId: string, params: RankingConfigParams): Promise<Sandbox> {
  const response = await apiClient.post<Sandbox>(`${PLANS}/${sessionId}/sandbox`, { params });
  return response.data;
}

/** Replays the newest complete plans with each plan's config (Baseline) and `params` (Tuned). */
export async function runImpact(params: RankingConfigParams, sample: ImpactSample): Promise<Impact> {
  const response = await apiClient.post<Impact>(`${PLANS}/impact`, { params, sample });
  return response.data;
}

export const navigatorTuningApi = {
  runImpact,
  listPlans,
  getPlan,
  runSandbox,
  listConfigVersions,
  getLiveConfigVersion,
  getDraftConfigVersion,
  createDraft,
  saveDraft,
  discardDraft,
  publishDraft,
  rollbackToVersion,
};
